import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useCopy } from './copy/copyMode';
import { WaitlistCTA } from './WaitlistCTA';
function FeatureItem({
  text
}: {
  text: string;
}) {
  return <li className="flex items-start gap-3 text-sm md:text-base text-[#2B211A]" data-id="element-1953">
      <div className="w-1.5 h-1.5 bg-[#2B211A] mt-2 shrink-0" data-id="element-1954" />
      <span className="leading-relaxed" data-id="element-1955">{text}</span>
    </li>;
}
export function PricingSection() {
  const copy = useCopy();
  return <section className="relative w-full bg-[#FAF8F4] py-16 md:py-32 overflow-hidden" data-id="element-1956">
      {/* Halftone dot-matrix texture — matches the other cream sections */}
      <div aria-hidden="true" className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-1957" />
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10" data-id="element-1958">
        {/* Header */}
        <div className="mb-12 md:mb-16 text-center md:text-left" data-id="element-1959">
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1]" data-id="element-1960">
            {copy.pricingSection.headline}
          </h2>
        </div>

        {/* Grid Container */}
        <div className="relative border-t border-b border-dotted border-neutral-300" data-id="element-1961">
          {/* Vertical dotted lines for desktop grid */}
          <div className="hidden md:block absolute top-0 bottom-0 left-1/3 w-px border-l border-dotted border-neutral-300" data-id="element-1962" />
          <div className="hidden md:block absolute top-0 bottom-0 left-2/3 w-px border-l border-dotted border-neutral-300" data-id="element-1963" />
          <div className="hidden md:block absolute top-0 bottom-0 left-0 w-px border-l border-dotted border-neutral-300" data-id="element-1964" />
          <div className="hidden md:block absolute top-0 bottom-0 right-0 w-px border-l border-dotted border-neutral-300" data-id="element-1965" />

          <div className="grid grid-cols-1 md:grid-cols-3 relative z-10" data-id="element-1966">
            {/* Starter Tier — paid, opens with a 7-day free trial */}
            <div className="p-6 md:p-8 border-b border-dotted border-neutral-300 md:border-b-0 flex flex-col" data-id="element-1967">
              <div className="bg-[#F3EFEA] rounded-2xl p-6 md:p-8 mb-8 h-48 flex flex-col justify-between" data-id="element-1968">
                <h3 className="font-title font-normal text-2xl text-[#2B211A]" data-id="element-1969">
                  Starter
                </h3>
                <div data-id="element-1970">
                  <div className="text-neutral-500 text-lg" data-id="element-1971">
                    <span className="text-2xl text-[#2B211A]" data-id="element-1972">$49</span>
                    /month
                  </div>
                  <span className="inline-flex mt-2 items-center gap-1.5 text-xs font-semibold text-[#EA580C] bg-[#FBEAE0] px-2.5 py-1 rounded-full" data-id="element-1973">
                    7-day free trial
                  </span>
                </div>
              </div>
              <ul className="space-y-3 mb-10 flex-1" data-id="element-1974">
                <FeatureItem text="1 workspace, unlimited members" data-id="element-1975" />
                <FeatureItem text="Starter monthly credit allowance" data-id="element-1976" />
                <FeatureItem text="Slack-native agent in threads + mentions" data-id="element-1977" />
                <FeatureItem text="Integrations + tool execution" data-id="element-1978" />
                <FeatureItem text="Scheduled tasks & crons" data-id="element-1979" />
                <FeatureItem text="Drafts + artifacts where supported" data-id="element-1980" />
              </ul>
              <WaitlistCTA className="w-full flex justify-center bg-[#EA580C] hover:bg-[#C2410C] text-white py-3 rounded-xl font-medium transition-colors" data-id="element-1981">
                Start 7-day trial
              </WaitlistCTA>
            </div>

            {/* Pro Tier — paid, opens with a 7-day free trial */}
            <div className="p-6 md:p-8 border-b border-dotted border-neutral-300 md:border-b-0 flex flex-col" data-id="element-1982">
              <div className="bg-[#F3EFEA] rounded-2xl p-6 md:p-8 mb-8 h-48 flex flex-col justify-between" data-id="element-1983">
                <h3 className="font-title font-normal text-2xl text-[#2B211A]" data-id="element-1984">
                  Pro
                </h3>
                <div data-id="element-1985">
                  <div className="text-neutral-500 text-lg" data-id="element-1986">
                    <span className="text-2xl text-[#2B211A]" data-id="element-1987">$199</span>
                    /month
                  </div>
                  <span className="inline-flex mt-2 items-center gap-1.5 text-xs font-semibold text-[#EA580C] bg-[#FBEAE0] px-2.5 py-1 rounded-full" data-id="element-1988">
                    7-day free trial
                  </span>
                </div>
              </div>
              <ul className="space-y-3 mb-10 flex-1" data-id="element-1989">
                <FeatureItem text="Multiple workspaces, unlimited members" data-id="element-1990" />
                <FeatureItem text="Higher monthly credit allowance" data-id="element-1991" />
                <FeatureItem text="Slack-native agent in threads + mentions" data-id="element-1992" />
                <FeatureItem text="Integrations + tool execution" data-id="element-1993" />
                <FeatureItem text="Scheduled tasks & crons" data-id="element-1994" />
                <FeatureItem text="Drafts + artifacts where supported" data-id="element-1995" />
              </ul>
              <WaitlistCTA className="w-full flex justify-center bg-[#2B211A] hover:bg-[#1C1510] text-white py-3 rounded-xl font-medium transition-colors" data-id="element-1996">
                Start 7-day trial
              </WaitlistCTA>
            </div>

            {/* Custom Tier — enterprise, contact sales */}
            <div className="p-6 md:p-8 flex flex-col" data-id="element-1997">
              <div className="bg-[#F3EFEA] rounded-2xl p-6 md:p-8 mb-8 h-48 flex flex-col justify-between" data-id="element-1998">
                <div data-id="element-1999">
                  <span className="block text-[11px] font-bold tracking-[2px] uppercase text-[#EA580C] mb-2" data-id="element-2000">
                    Enterprise
                  </span>
                  <h3 className="font-title font-normal text-2xl text-[#2B211A]" data-id="element-2001">
                    Custom
                  </h3>
                </div>
                <div className="text-neutral-500 text-lg" data-id="element-2002">Flexible pricing</div>
              </div>
              <ul className="space-y-3 mb-10 flex-1" data-id="element-2003">
                <FeatureItem text="Everything in Pro" data-id="element-2004" />
                <FeatureItem text="Invoicing + custom billing terms" data-id="element-2005" />
                <FeatureItem text="Security review support + DPA" data-id="element-2006" />
                <FeatureItem text="SLA + priority support" data-id="element-2007" />
                <FeatureItem text="Dedicated onboarding + tailored limits/controls" data-id="element-2008" />
              </ul>
              <Link to="/contact" className="w-full flex justify-center bg-[#2B211A] hover:bg-[#1C1510] text-white py-3 rounded-xl font-medium transition-colors" data-id="element-2009">
                Contact sales
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}